import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { oublierDockPos } from './components/dockPosition';
import { api, type Conversation, type ConversationsPage, type Message, postMessageStream } from './api';
import { useDictation } from './useDictation';

export const SIDEBAR_LIMIT = 20;

export type DisplayMsg = { id: string; role: 'user' | 'assistant' | 'tool' | 'error'; content: string };

/** La popin est fermée, déployée, ou repliée sur sa barre de titre. */
export type DockState = 'closed' | 'open' | 'minimized';

/**
 * La popin survit à un rechargement de page (F5, lien externe, retour arrière
 * dur) : sans ça, un simple rafraîchissement la faisait disparaître et il fallait
 * retrouver la conversation à la main dans l'historique. On ne garde que la
 * position (déployée / repliée) et l'id de la conversation — les messages sont
 * relus depuis le serveur, la saisie en cours n'est pas conservée.
 */
const DOCK_KEY = 'stm-chat-dock';
type PersistedDock = { state: 'open' | 'minimized'; conv: number | null };

function readPersistedDock(): PersistedDock | null {
  try {
    const raw = localStorage.getItem(DOCK_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (p?.state !== 'open' && p?.state !== 'minimized') return null;
    return { state: p.state, conv: typeof p.conv === 'number' ? p.conv : null };
  } catch {
    return null;
  }
}

type ChatCtx = {
  convs: Conversation[];
  totalConvs: number;
  activeId: number | null;
  activeTitle: string;
  messages: DisplayMsg[];
  input: string;
  setInput: (v: string) => void;
  attachments: File[];
  addFiles: (files: FileList | File[] | null) => void;
  removeAttachment: (i: number) => void;
  clearAttachments: () => void;
  streaming: boolean;
  dictation: ReturnType<typeof useDictation>;
  openConv: (id: number) => void;
  newConv: () => Promise<void>;
  delConv: (id: number) => Promise<void>;
  reloadSidebar: () => Promise<ConversationsPage>;
  send: () => Promise<void>;
  stop: () => void;
  dock: DockState;
  dockUnread: boolean;
  /** Incrémenté à chaque ouverture de la popin : donne le focus au composeur. */
  focusToken: number;
  openDock: (opts?: { conv?: number; draft?: string; fresh?: boolean }) => void;
  minimizeDock: () => void;
  closeDock: () => void;
};

const Ctx = createContext<ChatCtx | null>(null);

export function useChat(): ChatCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useChat hors de <ChatProvider>');
  return c;
}

/**
 * L'ÉTAT DU CHAT VIT AU-DESSUS DES ROUTES.
 *
 * La conversation doit survivre au passage page plein écran ↔ popin et à la
 * navigation d'un écran à l'autre : si l'état vivait dans <Chat/>, changer de
 * route démonterait le composant, ce qui couperait le flux SSE en cours et
 * viderait les messages à l'écran. Le provider est monté une fois dans <App/>,
 * après l'authentification ; la page et la popin n'en sont que deux rendus.
 */
export function ChatProvider({ children }: { children: ReactNode }) {
  // Lu une seule fois, avant le premier rendu : la popin doit être là dès la
  // première image, pas apparaître après un aller-retour serveur.
  const persistedRef = useRef<PersistedDock | null | undefined>(undefined);
  if (persistedRef.current === undefined) persistedRef.current = readPersistedDock();
  const persisted = persistedRef.current;

  const [convs, setConvs] = useState<Conversation[]>([]);
  const [totalConvs, setTotalConvs] = useState(0);
  const [activeId, setActiveId] = useState<number | null>(persisted?.conv ?? null);
  const [messages, setMessages] = useState<DisplayMsg[]>([]);
  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [dock, setDock] = useState<DockState>(persisted?.state ?? 'closed');
  const [dockUnread, setDockUnread] = useState(false);
  const [focusToken, setFocusToken] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  // Conversation créée à l'envoi : son chargement serveur écraserait le message
  // optimiste qu'on vient d'afficher.
  const skipLoadRef = useRef<number | null>(null);
  const dockRef = useRef<DockState>('closed');
  dockRef.current = dock;
  // Conversation RÉELLEMENT affichée au moment où un événement du flux arrive :
  // on peut changer de conversation (ou en ouvrir une neuve) pendant une réponse.
  const activeIdRef = useRef<number | null>(null);
  activeIdRef.current = activeId;
  const dictation = useDictation({ onText: setInput });

  function reloadSidebar() {
    return api.listConversations({ limit: SIDEBAR_LIMIT }).then((r) => {
      setConvs(r.items);
      setTotalConvs(r.total);
      return r;
    });
  }

  useEffect(() => {
    reloadSidebar().then((r) => {
      // Reprise d'une popin : on respecte ce qu'elle affichait, y compris une
      // conversation NEUVE (`conv: null`) — retomber sur la dernière discussion
      // serait exactement ce qu'« ouvrir vierge » cherche à éviter.
      if (persisted) return;
      setActiveId((cur) => cur ?? r.items[0]?.id ?? null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      if (dock === 'closed') localStorage.removeItem(DOCK_KEY);
      else localStorage.setItem(DOCK_KEY, JSON.stringify({ state: dock, conv: activeId }));
    } catch {
      /* mode privé / stockage plein : la popin ne survivra pas au rechargement, tant pis. */
    }
  }, [dock, activeId]);

  useEffect(() => {
    if (activeId == null) {
      setMessages([]);
      return;
    }
    if (skipLoadRef.current === activeId) {
      skipLoadRef.current = null;
      return;
    }
    api.listMessages(activeId)
      .then((rows: Message[]) => {
        setMessages(rows.map((r) => ({
          id: `m${r.id}`,
          role: r.role === 'system' ? 'assistant' : r.role,
          content: r.content,
        })));
      })
      // Conversation supprimée depuis (autre onglet, autre session) : la popin
      // restaurée repart sur un fil neuf plutôt que sur une erreur.
      .catch(() => {
        setMessages([]);
        setActiveId(null);
      });
  }, [activeId]);

  function openConv(id: number) {
    setActiveId(id);
  }

  async function newConv() {
    const c = await api.createConversation();
    setConvs((cs) => [c, ...cs].slice(0, SIDEBAR_LIMIT));
    setTotalConvs((n) => n + 1);
    setActiveId(c.id);
  }

  async function delConv(id: number) {
    if (!confirm('Supprimer cette conversation ?')) return;
    await api.deleteConversation(id);
    setConvs((cs) => cs.filter((c) => c.id !== id));
    setTotalConvs((n) => Math.max(0, n - 1));
    if (activeId === id) setActiveId(null);
  }

  function addFiles(files: FileList | File[] | null) {
    if (!files || files.length === 0) return;
    const arr = Array.from(files).slice(0, 10);
    setAttachments((cur) => [...cur, ...arr].slice(0, 10));
  }
  function removeAttachment(idx: number) {
    setAttachments((cur) => cur.filter((_, i) => i !== idx));
  }
  const clearAttachments = () => setAttachments([]);

  function openDock(opts?: { conv?: number; draft?: string; fresh?: boolean }) {
    // « Demander à l'IA » part d'une page blanche : reprendre le dernier échange
    // mêlerait la question du moment à un contexte sans rapport. La conversation
    // n'est créée côté serveur qu'à l'envoi — pas de coquille vide dans la liste.
    if (opts?.fresh) {
      // Une conversation neuve repart de l'emplacement d'origine : la fenêtre
      // peut avoir été glissée ailleurs pour un échange précédent, et « Demander
      // à l'IA » doit la ramener là où on l'attend.
      oublierDockPos();
      setActiveId(null);
      activeIdRef.current = null;
      setMessages([]);
      setInput('');
      setAttachments([]);
    }
    if (opts?.conv != null) {
      setActiveId(opts.conv);
      // Conversation ouverte depuis un écran (find-or-create sur un document
      // Odoo) : elle peut être absente de la liste, donc sans titre à afficher.
      if (!convs.some((c) => c.id === opts.conv)) reloadSidebar();
    }
    if (opts?.draft != null) setInput(opts.draft);
    setDock('open');
    setDockUnread(false);
    // Même quand la popin est DÉJÀ ouverte (on enchaîne deux fiches), le composeur
    // doit reprendre le focus, curseur en fin de brouillon.
    setFocusToken((t) => t + 1);
  }
  const minimizeDock = () => setDock('minimized');
  function closeDock() {
    setDock('closed');
    setDockUnread(false);
  }

  async function send() {
    if ((!input.trim() && attachments.length === 0) || streaming) return;
    dictation.stop();
    let convId = activeId;
    if (convId == null) {
      const c = await api.createConversation();
      setConvs((cs) => [c, ...cs].slice(0, SIDEBAR_LIMIT));
      setTotalConvs((n) => n + 1);
      skipLoadRef.current = c.id;
      setActiveId(c.id);
      activeIdRef.current = c.id;
      convId = c.id;
    }

    // Upload attachments first (if any) and prefix the message with their paths.
    let content = input;
    if (attachments.length > 0) {
      try {
        const { attachments: saved } = await api.uploadAttachments(convId, attachments);
        const lines = saved.map((a) => `- attachments/${a.name}`).join('\n');
        const prefix = `J'ai joint ${saved.length === 1 ? 'ce document' : 'ces documents'} à mon message (lisibles via l'outil Read depuis le cwd) :\n${lines}\n\n`;
        content = prefix + (input.trim() ? input : '');
      } catch (err: any) {
        setMessages((m) => [...m, { id: `e${Date.now()}`, role: 'error', content: 'Échec de l\'upload : ' + err.message }]);
        return;
      }
    }

    const displayedUserContent = attachments.length > 0
      ? `📎 ${attachments.map((a) => a.name).join(', ')}${input.trim() ? '\n\n' + input : ''}`
      : input;
    const userMsg: DisplayMsg = { id: `u${Date.now()}`, role: 'user', content: displayedUserContent };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setAttachments([]);
    setStreaming(true);

    // Track the currently "open" assistant bubble. A tool_use event closes
    // it so the next text delta starts a fresh bubble below the tool calls —
    // preserves the chronological order text → tools → text → tools → final text.
    let activeAssistantId: string | null = null;

    const ac = new AbortController();
    abortRef.current = ac;

    try {
      await postMessageStream(convId, content, (ev) => {
        // Le titre alimente la liste, il vaut quelle que soit la vue courante.
        if (ev.event === 'title') {
          const newTitle = ev.data.title as string;
          setConvs((cs) => cs.map((c) => (c.id === convId ? { ...c, title: newTitle } : c)));
          return;
        }
        // Le reste écrit dans le fil : si l'utilisateur a changé de conversation
        // entre-temps, ces messages n'ont rien à faire sous ses yeux. Ils sont
        // persistés côté serveur et reparaissent en rouvrant la conversation.
        if (activeIdRef.current !== convId) return;
        if (ev.event === 'text') {
          const delta = ev.data.delta ?? '';
          // Popin repliée : signaler qu'une réponse est arrivée dans le dos.
          if (dockRef.current === 'minimized') setDockUnread(true);
          if (activeAssistantId) {
            const id = activeAssistantId;
            setMessages((m) => m.map((x) => (x.id === id ? { ...x, content: x.content + delta } : x)));
          } else {
            const id = `a${Date.now()}-${Math.random()}`;
            activeAssistantId = id;
            setMessages((m) => [...m, { id, role: 'assistant', content: delta }]);
          }
        } else if (ev.event === 'tool_use') {
          activeAssistantId = null;
          setMessages((m) => [...m, { id: `t${Date.now()}-${Math.random()}`, role: 'tool', content: `→ ${ev.data.name}(${JSON.stringify(ev.data.input).slice(0, 200)})` }]);
        } else if (ev.event === 'error') {
          setMessages((m) => [...m, { id: `e${Date.now()}`, role: 'error', content: ev.data.message }]);
        }
      }, ac.signal);
    } catch (err: any) {
      // Don't surface AbortError as a message — the user knows they hit Stop.
      if (err?.name !== 'AbortError') {
        setMessages((m) => [...m, { id: `e${Date.now()}`, role: 'error', content: err.message }]);
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
      // Refresh conversation list (bumps updated_at order) and reload messages
      // so the persisted assistant content (incl. "[interrompu]" marker if any) shows.
      reloadSidebar();
      if (convId != null && activeIdRef.current === convId) api.listMessages(convId).then((rows: Message[]) => {
        setMessages(rows.map((r) => ({ id: `m${r.id}`, role: r.role === 'system' ? 'assistant' : r.role, content: r.content })));
      });
    }
  }

  function stop() {
    abortRef.current?.abort();
  }

  const activeTitle = convs.find((c) => c.id === activeId)?.title ?? '';

  const value: ChatCtx = {
    convs, totalConvs, activeId, activeTitle, messages,
    input, setInput,
    attachments, addFiles, removeAttachment, clearAttachments,
    streaming, dictation,
    openConv, newConv, delConv, reloadSidebar,
    send, stop,
    dock, dockUnread, focusToken, openDock, minimizeDock, closeDock,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
